import React, { useState } from 'react'
import { css } from '@emotion/core'
import styled from '@emotion/styled'
import { Formik, FormikProps } from 'formik'
import moment from 'moment'
import { Button, Checkbox, colors, Spinner, Table, Typography } from '@dw/helix'
import { PurchaseOrder } from './GenerateSerialNumbersPage'
import { Pagination } from '../../components'
import { checkboxStyles, columnCheckboxStyles, linkStyles, tableStyles, withEllipsisStyles } from './styles'

const buttonStyles = css`
  margin-left: 35px;
`

const GenerateBlock = styled.div<any>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 20px;
  color: ${(p: any) => p.disabled && `${colors.grey1}`};
`

interface ToBeGeneratedFormProps {
  showPOInfo: (item: PurchaseOrder) => void
  searchQuery: string
  showConfirmSNGForm: (data: any, helpers: any) => void
  initialValues: object
  purchaseOrders: PurchaseOrder[]
  requestIsProcessing: boolean
}

const ToBeGeneratedForm: React.FunctionComponent<ToBeGeneratedFormProps> = ({
  showPOInfo,
  searchQuery,
  showConfirmSNGForm,
  initialValues,
  purchaseOrders,
  requestIsProcessing,
}) => {
  const [page, setPage] = useState(1)
  const [field, setField] = useState<string>('')
  const [ascending, setAscending] = useState(false)

  const filteredPurchaseOrders = purchaseOrders
    .filter(({ orderId, vendorName, serialnumberStatus }) => {
      // show only new sorted by search query items
      return (
        serialnumberStatus === 'NEW' &&
        ((orderId && orderId.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (vendorName && vendorName.toLowerCase().includes(searchQuery.toLowerCase())))
      )
    })
    .sort((a, b) => {
      // @ts-ignore
      if (a[field] > b[field]) {
        return ascending ? 1 : -1
      }

      // @ts-ignore
      if (a[field] < b[field]) {
        return ascending ? -1 : 1
      }

      return 0
    })

  const onSortClick = (newField: string, newAscending: boolean) => {
    setField(newField)
    setAscending(newAscending)
  }

  const currentPageItems = filteredPurchaseOrders.filter((item, index) => index >= 20 * (page - 1) && index < 20 * page)

  return (
    <Formik initialValues={initialValues} onSubmit={showConfirmSNGForm}>
      {({ handleSubmit, setFieldValue, values }: FormikProps<any>) => {
        const checked = Object.values(values).filter(order => order).length
        return (
          <form onSubmit={handleSubmit}>
            <Table
              sortable
              items={currentPageItems}
              columns={[
                {
                  label: (
                    <Checkbox
                      css={columnCheckboxStyles}
                      name="allCheckboxes"
                      checked={currentPageItems.filter(item => !values[item.orderId]).length === 0}
                      onChange={value => {
                        currentPageItems.forEach(item => {
                          setFieldValue(item.orderId, value)
                        })
                      }}
                    />
                  ),
                  render: item => (
                    <Checkbox
                      css={checkboxStyles}
                      name={`${item.orderId}`}
                      checked={values[item.orderId]}
                      onChange={value => {
                        setFieldValue(item.orderId, value)
                      }}
                    />
                  ),
                  width: '64px',
                },
                {
                  label: 'PO Number',
                  sortable: true,
                  field: 'orderId',
                  width: '116px',
                },
                {
                  label: 'Created Date',
                  sortable: true,
                  field: 'createdAt',
                  render: ({ createdAt }) => moment(createdAt).format('YYYY-MM-DD'),
                  width: '102px',
                },
                {
                  label: 'Supplier',
                  sortable: true,
                  field: 'vendorName',
                  render: ({ vendorName }) => (
                    <span title={vendorName} css={withEllipsisStyles}>
                      {vendorName}
                    </span>
                  ),
                  width: '122px',
                },
                { label: 'NAV-status', sortable: true, field: 'navStatus', width: '83px' },
                {
                  width: '90px',
                  render: item => (
                    <Button theme="tertiary" css={linkStyles} onClick={() => showPOInfo(item)} type="button">
                      View
                    </Button>
                  ),
                },
              ]}
              rowKey={'orderId'}
              css={tableStyles}
              onSortClick={onSortClick}
            />
            {requestIsProcessing && currentPageItems.length === 0 && (
              <Spinner
                color={colors.brandBlue}
                css={css`
                  padding: 10px;
                `}
              />
            )}
            <GenerateBlock disabled={checked === 0}>
              <Typography
                css={css`
                  min-width: 182px;
                `}
              >
                {checked === 0 ? 'No' : `${checked}`} purchase order{checked === 1 ? '' : 's'} selected.
              </Typography>
              <Button css={buttonStyles} type="submit" disabled={checked === 0}>
                Generate Serials
              </Button>
            </GenerateBlock>
            {filteredPurchaseOrders.length > 0 && (
              <Pagination
                total={filteredPurchaseOrders.length}
                perPage={20}
                onChange={setPage}
                css={css`
                  margin-top: 25px;
                `}
              />
            )}
          </form>
        )
      }}
    </Formik>
  )
}

export default ToBeGeneratedForm
